import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import useUsageLimits from '../hooks/useUsageLimits';
import UsageBadge from '../components/UsageBadge';
import PaywallModal from '../components/PaywallModal';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Sparkles, 
  Loader2, 
  Download,
  Upload,
  Video,
  Image as ImageIcon,
  Volume2,
  X,
  Play,
  Pause,
  Youtube,
  Instagram,
  Plus,
  FolderOpen,
  Hash,
  Copy,
  Check,
  ExternalLink,
  Settings,
  Music,
  SkipBack,
  SkipForward,
  Lock,
  Crown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const videoFormats = [
  { value: 'youtube_short', label: 'YouTube Short', aspect: '9:16', duration: '60 sec', icon: '📱' },
  { value: 'instagram_reel', label: 'Instagram Reel', aspect: '9:16', duration: '60 sec', icon: '📸' },
  { value: 'youtube_video', label: 'YouTube Video (5-10 min)', aspect: '16:9', duration: '5-10 min', icon: '🎬' },
];

export default function VideoGenerator() {
  const { currentBrand } = useBrand();
  const [videoFormat, setVideoFormat] = useState('youtube_short');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [audioFile, setAudioFile] = useState(null);
  const [audioBase64, setAudioBase64] = useState('');
  const [audioName, setAudioName] = useState('');
  const [imageDuration, setImageDuration] = useState([3]);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingYoutube, setUploadingYoutube] = useState(false);
  
  // Queue status states
  const [videoStatus, setVideoStatus] = useState(null); // 'queued', 'processing', 'uploading'
  const [queuePosition, setQueuePosition] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  
  // Library modal states
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [showAudioLibrary, setShowAudioLibrary] = useState(false);
  const [libraryImages, setLibraryImages] = useState([]);
  const [libraryAudios, setLibraryAudios] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  
  // Hashtag states
  const [hashtags, setHashtags] = useState([]);
  const [hashtagTips, setHashtagTips] = useState('');
  const [hashtagPlatform, setHashtagPlatform] = useState('all');
  const [loadingHashtags, setLoadingHashtags] = useState(false);
  const [copiedHashtags, setCopiedHashtags] = useState(false);
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Audio-Image Sync Mode
  const [syncMode, setSyncMode] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const audioPlayerRef = useRef(null);
  
  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // Free-tier usage limits
  const navigate = useNavigate();
  const { limits: usageLimits, refresh: fetchUsageLimits, isLocked } = useUsageLimits();
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallCategory, setPaywallCategory] = useState(null); // 'youtube' or 'reel'

  // Determine if currently-selected format is locked for this user
  const isReelFormat = videoFormat === 'instagram_reel';
  const isYouTubeFormat = videoFormat === 'youtube_short' || videoFormat === 'youtube_video';
  const youtubeLocked = isLocked('youtube');
  const reelLocked = isLocked('reel');
  const currentFormatLocked = (isYouTubeFormat && youtubeLocked) || (isReelFormat && reelLocked);
  const lockedCategory = isYouTubeFormat ? 'youtube' : (isReelFormat ? 'reel' : null);

  // Calculate which image should be shown at current audio time
  useEffect(() => {
    if (images.length > 0 && imageDuration[0] > 0) {
      const index = Math.floor(currentTime / imageDuration[0]);
      setCurrentImageIndex(Math.min(index, images.length - 1));
    }
  }, [currentTime, imageDuration, images.length]);

  // Audio player controls
  const handleAudioTimeUpdate = () => {
    if (audioPlayerRef.current) {
      setCurrentTime(audioPlayerRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioPlayerRef.current) {
      setAudioDuration(audioPlayerRef.current.duration);
    }
  };

  const togglePlayPause = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const seekAudio = (time) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const jumpToImage = (index) => {
    const time = index * imageDuration[0];
    seekAudio(time);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Drag and drop handlers for image reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    
    // Remove from old position
    newImages.splice(draggedIndex, 1);
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);
    
    setImages(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
    toast.success(`Image moved to position ${dropIndex + 1}`);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Fetch library content
  const fetchLibraryImages = async () => {
    setLoadingLibrary(true);
    try {
      const response = await axios.get(`${API}/api/content?content_type=image&limit=50`, { headers: getAuthHeaders() });
      // Fetch full image data for each
      const imagesWithData = await Promise.all(
        response.data.map(async (img) => {
          try {
            const detail = await axios.get(`${API}/api/content/image/${img.id}`, { headers: getAuthHeaders() });
            return { ...img, image_base64: detail.data.image_base64 };
          } catch {
            return img;
          }
        })
      );
      setLibraryImages(imagesWithData.filter(img => img.image_base64));
    } catch (error) {
      toast.error('Failed to load images');
    } finally {
      setLoadingLibrary(false);
    }
  };

  const fetchLibraryAudios = async () => {
    setLoadingLibrary(true);
    try {
      const response = await axios.get(`${API}/api/content?content_type=audio&limit=50`, { headers: getAuthHeaders() });
      // Fetch full audio data for each
      const audiosWithData = await Promise.all(
        response.data.map(async (audio) => {
          try {
            const detail = await axios.get(`${API}/api/content/audio/${audio.id}`, { headers: getAuthHeaders() });
            return { ...audio, audio_base64: detail.data.audio_base64 };
          } catch {
            return audio;
          }
        })
      );
      setLibraryAudios(audiosWithData.filter(a => a.audio_base64));
    } catch (error) {
      toast.error('Failed to load audios');
    } finally {
      setLoadingLibrary(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          name: file.name,
          preview: event.target.result,
          base64: event.target.result.split(',')[1]
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const addImageFromLibrary = (img) => {
    setImages(prev => [...prev, {
      id: Date.now() + Math.random(),
      name: img.title,
      preview: `data:image/png;base64,${img.image_base64}`,
      base64: img.image_base64
    }]);
    toast.success('Image added');
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFile(file);
      setAudioName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        setAudioBase64(event.target.result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    }
  };

  const addAudioFromLibrary = (audio) => {
    setAudioBase64(audio.audio_base64);
    setAudioName(audio.title);
    setAudioFile(null);
    setShowAudioLibrary(false);
    toast.success('Audio added');
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const removeAudio = () => {
    setAudioBase64('');
    setAudioName('');
    setAudioFile(null);
  };

  const handleGenerateVideo = async () => {
    if (images.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    // Free-tier guard: block locally before hitting the server
    if (currentFormatLocked) {
      setPaywallCategory(lockedCategory);
      setShowPaywall(true);
      toast.error(
        lockedCategory === 'youtube'
          ? 'Free limit reached. Contact support to upgrade.'
          : 'Free limit reached. Contact support to upgrade.'
      );
      return;
    }

    setLoading(true);
    setGeneratedVideo(null);
    setVideoStatus('starting');
    setQueuePosition(null);
    setProgress(0);
    setStatusMessage('Starting video generation...');

    try {
      // Start video generation (returns immediately with task ID)
      const response = await axios.post(
        `${API}/api/video/generate`,
        {
          images: images.map(img => img.base64),
          audio_base64: audioBase64 || null,
          format: videoFormat,
          image_duration: imageDuration[0],
          title: title || 'Untitled Video'
        },
        { headers: getAuthHeaders(), timeout: 30000 } // 30 sec to start task
      );
      
      const taskId = response.data.task_id;
      const initialQueuePos = response.data.queue_position;
      
      if (initialQueuePos > 0) {
        setVideoStatus('queued');
        setQueuePosition(initialQueuePos);
        setStatusMessage(`Position ${initialQueuePos} in queue`);
        toast.info(`Video queued at position ${initialQueuePos}. Please wait...`);
      } else {
        setVideoStatus('processing');
        toast.info('Video generation started! Please wait...');
      }
      
      // Poll for status
      let attempts = 0;
      const maxAttempts = 360; // 30 minutes max (5 sec intervals)
      
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/api/video/status/${taskId}`,
            { headers: getAuthHeaders() }
          );
          
          const status = statusResponse.data;
          
          // Update UI with current status
          setVideoStatus(status.status);
          setProgress(status.progress || 0);
          
          if (status.queue_position) {
            setQueuePosition(status.queue_position);
            setStatusMessage(status.message || `Position ${status.queue_position} in queue`);
          } else if (status.status === 'processing') {
            setQueuePosition(null);
            setStatusMessage(`Processing... ${status.progress || 0}%`);
          } else if (status.status === 'uploading') {
            setStatusMessage('Uploading to cloud storage...');
          }
          
          if (status.status === 'completed') {
            setGeneratedVideo({
              id: status.video_id,
              title: status.title,
              format: status.format,
              video_url: status.video_url,
              file_size: status.file_size
            });
            toast.success('Video generated successfully!');
            setLoading(false);
            setVideoStatus(null);
            fetchUsageLimits(); // refresh free-tier counters
            return;
          } else if (status.status === 'failed') {
            toast.error(status.error || 'Video generation failed');
            setLoading(false);
            setVideoStatus(null);
            return;
          } else {
            // Still processing or queued
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollStatus, 5000); // Poll every 5 seconds
            } else {
              toast.error('Video generation timed out. Check Content Library.');
              setLoading(false);
              setVideoStatus(null);
            }
          }
        } catch (err) {
          console.error('Status poll error:', err);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 5000);
          } else {
            toast.error('Could not get video status. Check Content Library.');
            setLoading(false);
            setVideoStatus(null);
          }
        }
      };
      
      // Start polling after 3 seconds
      setTimeout(pollStatus, 3000);
      
    } catch (error) {
      console.error('Video generation error:', error);
      // Backend 403 with structured detail = free-tier limit hit
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'FREE_TIER_LIMIT_REACHED') {
        setPaywallCategory(detail.category);
        setShowPaywall(true);
        toast.error('Free limit reached. Contact support to upgrade.');
        fetchUsageLimits();
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to start video generation.');
        toast.error(msg);
      }
      setLoading(false);
      setVideoStatus(null);
    }
  };

  const handleDownload = () => {
    if (!generatedVideo) return;
    
    // Use the video_id to construct download URL
    const videoId = generatedVideo.id || generatedVideo.video_id;
    if (videoId) {
      // Open download URL directly (no auth needed)
      const downloadUrl = `${API}/api/video/download/${videoId}`;
      window.open(downloadUrl, '_blank');
      toast.success('Download started!');
    } else if (generatedVideo.video_url) {
      // Fallback to video_url
      window.open(`${API}${generatedVideo.video_url}`, '_blank');
      toast.success('Download started!');
    } else {
      toast.error('Video not available for download');
    }
  };

  const handleYouTubeUpload = async () => {
    if (!generatedVideo) {
      toast.error('Please generate a video first');
      return;
    }
    setUploadingYoutube(true);
    try {
      const response = await axios.post(
        `${API}/api/youtube/upload`,
        {
          video_id: generatedVideo.id,
          title: title || 'Untitled Video',
          description: description || '',
          privacy: 'private'
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.auth_url) {
        window.open(response.data.auth_url, '_blank', 'width=600,height=700');
        toast.info('Please complete YouTube authentication in the popup');
      } else {
        toast.success('Video uploaded to YouTube!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload to YouTube');
    } finally {
      setUploadingYoutube(false);
    }
  };

  const selectedFormat = videoFormats.find(f => f.value === videoFormat);

  // Generate hashtags for maximum reach
  const handleGenerateHashtags = async () => {
    if (!title && !description) {
      toast.error('Please add a title or description first');
      return;
    }

    setLoadingHashtags(true);
    try {
      const response = await axios.post(
        `${API}/api/content/hashtags`,
        {
          topic: title || 'video content',
          platform: hashtagPlatform,
          content_description: description || undefined
        },
        { headers: getAuthHeaders() }
      );
      
      setHashtags(response.data.hashtags);
      setHashtagTips(response.data.tips);
      toast.success('Hashtags generated!');
    } catch (error) {
      console.error('Hashtag generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate hashtags');
    } finally {
      setLoadingHashtags(false);
    }
  };

  const copyHashtags = () => {
    const hashtagText = hashtags.join(' ');
    navigator.clipboard.writeText(hashtagText);
    setCopiedHashtags(true);
    toast.success('Hashtags copied to clipboard!');
    setTimeout(() => setCopiedHashtags(false), 2000);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-zinc-100 font-['Manrope']">Video Generator</h1>
        <p className="text-zinc-400 mt-1">Create videos from images and audio for YouTube & Instagram</p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          <div className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 space-y-6">
            {/* Video Format */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Video className="w-4 h-4" />
                Video Format
              </Label>
              <Select value={videoFormat} onValueChange={setVideoFormat}>
                <SelectTrigger className="bg-zinc-900/50 border-zinc-800 h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {videoFormats.map((format) => (
                    <SelectItem key={format.value} value={format.value}>
                      <div className="flex items-center gap-2">
                        <span>{format.icon}</span>
                        <span>{format.label}</span>
                        <span className="text-xs text-zinc-500">({format.aspect})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Video Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter video title..."
                className="bg-zinc-900/50 border-zinc-800 h-12"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Video description..."
                className="bg-zinc-900/50 border-zinc-800 min-h-[80px] resize-none"
              />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Images ({images.length} selected)
              </Label>
              <div className="flex gap-2">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => imageInputRef.current?.click()}
                  className="flex-1 h-12 border-zinc-700 hover:border-violet-500"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  From Computer
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowImageLibrary(true); fetchLibraryImages(); }}
                  className="flex-1 h-12 border-zinc-700 hover:border-violet-500"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  From Library
                </Button>
              </div>
              
              {/* Image Previews - Drag to Reorder */}
              {images.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-zinc-500 mb-2">Drag images to reorder</p>
                  <div className="flex flex-wrap gap-2">
                    {images.map((img, index) => (
                      <div 
                        key={img.id} 
                        className={`relative group cursor-move transition-all duration-200 ${
                          draggedIndex === index ? 'opacity-50 scale-95' : ''
                        } ${
                          dragOverIndex === index ? 'ring-2 ring-violet-500 ring-offset-2 ring-offset-zinc-900' : ''
                        }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        <img
                          src={img.preview}
                          alt={`Upload ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border border-zinc-700 pointer-events-none"
                        />
                        <button
                          onClick={() => removeImage(img.id)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                        <span className="absolute bottom-1 left-1 text-xs bg-black/70 px-1.5 py-0.5 rounded font-medium">{index + 1}</span>
                        <div className="absolute inset-0 bg-violet-500/0 group-hover:bg-violet-500/10 rounded-lg transition-colors pointer-events-none" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Audio Upload */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Volume2 className="w-4 h-4" />
                Audio (Optional)
              </Label>
              <div className="flex gap-2">
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => audioInputRef.current?.click()}
                  className="flex-1 h-12 border-zinc-700 hover:border-violet-500"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  From Computer
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowAudioLibrary(true); fetchLibraryAudios(); }}
                  className="flex-1 h-12 border-zinc-700 hover:border-violet-500"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  From Library
                </Button>
              </div>
              
              {audioBase64 && (
                <div className="flex items-center gap-2 mt-2 p-3 bg-zinc-800/50 rounded-lg">
                  <Volume2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-zinc-300 flex-1 truncate">{audioName}</span>
                  <button onClick={removeAudio} className="text-zinc-500 hover:text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* Sync Mode Button - Only show when both audio and images exist */}
              {audioBase64 && images.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSyncMode(true)}
                  className="w-full mt-2 h-10 border-violet-500/50 hover:border-violet-500 hover:bg-violet-500/10"
                >
                  <Music className="w-4 h-4 mr-2" />
                  Open Audio-Image Sync Mode
                </Button>
              )}
            </div>

            {/* Audio-Image Sync Mode Panel */}
            {syncMode && audioBase64 && images.length > 0 && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-violet-900/20 to-purple-900/20 border border-violet-500/30 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-violet-300 flex items-center gap-2">
                    <Music className="w-4 h-4" />
                    Audio-Image Sync Mode
                  </h3>
                  <button 
                    onClick={() => { setSyncMode(false); setIsPlaying(false); if(audioPlayerRef.current) audioPlayerRef.current.pause(); }}
                    className="text-zinc-500 hover:text-zinc-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Hidden Audio Element */}
                <audio
                  ref={audioPlayerRef}
                  src={`data:audio/mp3;base64,${audioBase64}`}
                  onTimeUpdate={handleAudioTimeUpdate}
                  onLoadedMetadata={handleAudioLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                />
                
                {/* Audio Controls */}
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => seekAudio(Math.max(0, currentTime - 5))}
                    className="h-9 w-9 p-0 border-zinc-700"
                  >
                    <SkipBack className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={togglePlayPause}
                    className="h-10 w-10 p-0 rounded-full"
                    style={{ background: currentBrand.primary }}
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => seekAudio(Math.min(audioDuration, currentTime + 5))}
                    className="h-9 w-9 p-0 border-zinc-700"
                  >
                    <SkipForward className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 text-sm text-zinc-400">
                    {formatTime(currentTime)} / {formatTime(audioDuration)}
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div 
                  className="h-2 bg-zinc-800 rounded-full cursor-pointer relative overflow-hidden"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percent = (e.clientX - rect.left) / rect.width;
                    seekAudio(percent * audioDuration);
                  }}
                >
                  <div 
                    className="h-full rounded-full transition-all duration-100"
                    style={{ 
                      width: `${(currentTime / audioDuration) * 100}%`,
                      background: `linear-gradient(90deg, ${currentBrand.primary}, ${currentBrand.secondary})`
                    }}
                  />
                  {/* Image markers on timeline */}
                  {images.map((_, index) => {
                    const markerPos = ((index * imageDuration[0]) / audioDuration) * 100;
                    if (markerPos > 100) return null;
                    return (
                      <div
                        key={index}
                        className="absolute top-0 bottom-0 w-0.5 bg-zinc-500/50"
                        style={{ left: `${markerPos}%` }}
                        title={`Image ${index + 1}`}
                      />
                    );
                  })}
                </div>
                
                {/* Current Image Indicator */}
                <div className="text-center py-2">
                  <span className="text-xs text-zinc-500">Currently showing:</span>
                  <span className="ml-2 text-sm font-medium" style={{ color: currentBrand.primary }}>
                    Image {currentImageIndex + 1} of {images.length}
                  </span>
                </div>
                
                {/* Sync Timeline - Images with drag/drop */}
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500">Drag to reorder while listening:</p>
                  <div className="flex flex-wrap gap-2">
                    {images.map((img, index) => {
                      const isCurrentImage = index === currentImageIndex;
                      return (
                        <div 
                          key={img.id} 
                          className={`relative cursor-move transition-all duration-200 ${
                            draggedIndex === index ? 'opacity-50 scale-95' : ''
                          } ${
                            dragOverIndex === index ? 'ring-2 ring-violet-500' : ''
                          } ${
                            isCurrentImage ? 'ring-2 ring-green-500 scale-105' : ''
                          }`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          onClick={() => jumpToImage(index)}
                          title={`Click to jump to ${formatTime(index * imageDuration[0])}`}
                        >
                          <img
                            src={img.preview}
                            alt={`Image ${index + 1}`}
                            className="w-14 h-14 object-cover rounded-lg border border-zinc-700 pointer-events-none"
                          />
                          <span className={`absolute bottom-0.5 left-0.5 text-[10px] px-1 rounded font-bold ${
                            isCurrentImage ? 'bg-green-500 text-white' : 'bg-black/70 text-white'
                          }`}>
                            {index + 1}
                          </span>
                          {isCurrentImage && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <p className="text-xs text-zinc-600 text-center">
                  Click an image to jump to that point • Green = currently playing
                </p>
              </div>
            )}

            {/* Image Duration Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Duration per Image</Label>
                <span className="text-sm text-zinc-500">{imageDuration[0]} seconds</span>
              </div>
              <Slider
                value={imageDuration}
                onValueChange={setImageDuration}
                min={1}
                max={15}
                step={0.5}
                className="w-full"
              />
              <p className="text-xs text-zinc-600">
                Estimated video length: {(images.length * imageDuration[0]).toFixed(1)}s
                {audioBase64 && " (will match audio length if longer)"}
              </p>
            </div>

            {/* Generate Button */}
            <div className="space-y-2">
              {/* Free-tier usage indicator */}
              {lockedCategory && (
                <UsageBadge
                  limits={usageLimits}
                  category={lockedCategory}
                  onUpgradeClick={() => { setPaywallCategory(lockedCategory); setShowPaywall(true); }}
                />
              )}

              <Button
                onClick={handleGenerateVideo}
                disabled={loading || images.length === 0 || currentFormatLocked}
                title={currentFormatLocked
                  ? (lockedCategory === 'youtube'
                      ? 'Free limit reached. You have used your 1 free YouTube video. Upgrade to Pro to continue.'
                      : 'Free limit reached. You have used your 1 free Reel. Upgrade to Pro to continue.')
                  : ''}
                className="w-full h-12 rounded-xl font-medium transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={ currentFormatLocked
                  ? { background: 'linear-gradient(135deg, #52525b 0%, #3f3f46 100%)' }
                  : { background: `linear-gradient(135deg, ${currentBrand.primary} 0%, ${currentBrand.secondary} 100%)` }
                }
                data-testid="generate-video-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {videoStatus === 'queued'
                      ? `Queued #${queuePosition || '...'}`
                      : videoStatus === 'uploading'
                      ? 'Uploading...'
                      : `Processing ${progress > 0 ? progress + '%' : '...'}`
                    }
                  </span>
                ) : currentFormatLocked ? (
                  <span className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    {lockedCategory === 'youtube' ? 'YouTube limit reached — Upgrade' : 'Reel limit reached — Upgrade'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Generate Video
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Info */}
          <div className="p-4 rounded-xl bg-zinc-900/30 border border-zinc-800">
            <h3 className="text-sm font-medium text-zinc-300 mb-2">Tips</h3>
            <ul className="text-xs text-zinc-500 space-y-1">
              <li>• YouTube Short/Instagram Reel: 1080x1920 (9:16 vertical)</li>
              <li>• YouTube Video: 1920x1080 (16:9 horizontal)</li>
              <li>• Images play in order uploaded</li>
              <li>• Video adjusts to audio length automatically</li>
            </ul>
          </div>
        </motion.div>

        {/* Output Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-100 font-['Manrope']">Generated Video</h2>
              {generatedVideo && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  className="text-zinc-400 hover:text-zinc-200"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download MP4
                </Button>
              )}
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center min-h-[400px]">
                <div className="text-center max-w-sm">
                  {videoStatus === 'queued' ? (
                    <>
                      {/* Queue status */}
                      <div className="w-16 h-16 rounded-full border-4 border-zinc-700 flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl font-bold" style={{ color: currentBrand.primary }}>
                          {queuePosition || '?'}
                        </span>
                      </div>
                      <p className="text-lg font-medium text-zinc-300">
                        {queuePosition === 1 ? "You're next!" : `Position ${queuePosition} in queue`}
                      </p>
                      <p className="text-sm text-zinc-500 mt-2">
                        Server is processing other videos. Your video will start soon.
                      </p>
                      <div className="mt-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                        <p className="text-xs text-zinc-400">
                          Max 2 videos process at a time to ensure quality
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Processing status */}
                      <Loader2 
                        className="w-12 h-12 animate-spin mx-auto mb-4"
                        style={{ color: currentBrand.primary }}
                      />
                      <p className="text-zinc-300 font-medium">
                        {videoStatus === 'uploading' ? 'Uploading to cloud...' : 'Generating video...'}
                      </p>
                      
                      {/* Progress bar */}
                      {progress > 0 && (
                        <div className="mt-4 w-full">
                          <div className="flex justify-between text-xs text-zinc-500 mb-1">
                            <span>Progress</span>
                            <span>{progress}%</span>
                          </div>
                          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${progress}%`,
                                background: `linear-gradient(90deg, ${currentBrand.primary}, ${currentBrand.secondary})`
                              }}
                            />
                          </div>
                        </div>
                      )}
                      
                      <p className="text-sm text-zinc-500 mt-3">
                        {statusMessage || 'This may take 2-5 minutes for longer videos'}
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : generatedVideo ? (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 flex items-center justify-center bg-black rounded-lg overflow-hidden">
                  <video
                    src={generatedVideo.video_url 
                      ? `${API}${generatedVideo.video_url}` 
                      : `data:video/mp4;base64,${generatedVideo.video_base64}`
                    }
                    controls
                    className="max-w-full max-h-[400px]"
                    style={{ aspectRatio: selectedFormat?.aspect === '9:16' ? '9/16' : '16/9' }}
                  />
                </div>
                
                {/* File size info */}
                {generatedVideo.file_size && (
                  <p className="text-xs text-zinc-500 text-center mt-2">
                    File size: {(generatedVideo.file_size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                )}
                
                {/* Upload Actions */}
                <div className="mt-4 space-y-3">
                  <p className="text-xs text-zinc-500 mb-2">Upload directly to your connected accounts</p>
                  <Button
                    onClick={handleYouTubeUpload}
                    disabled={uploadingYoutube}
                    className="w-full h-11 bg-red-600 hover:bg-red-500"
                    data-testid="upload-youtube-btn"
                  >
                    {uploadingYoutube ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Youtube className="w-5 h-5 mr-2" />
                    )}
                    Upload to YouTube
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handleDownload}
                      variant="outline"
                      className="h-11 border-zinc-700 hover:border-violet-500"
                      data-testid="download-instagram-btn"
                    >
                      <Instagram className="w-4 h-4 mr-2" />
                      Download for IG
                    </Button>
                    <Button
                      onClick={handleDownload}
                      variant="outline"
                      className="h-11 border-zinc-700 hover:border-zinc-600"
                      data-testid="download-tiktok-btn"
                    >
                      <TikTokIcon className="w-4 h-4 mr-2" />
                      Download for TT
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-600 text-center">
                    Connect accounts in <a href="/settings" className="text-violet-400 hover:underline">Settings</a> for direct upload
                  </p>
                </div>

                {/* Hashtag Generator */}
                <div className="mt-6 pt-6 border-t border-zinc-800">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      Generate Hashtags
                    </h3>
                    <Select value={hashtagPlatform} onValueChange={setHashtagPlatform}>
                      <SelectTrigger className="w-32 h-8 text-xs bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">All Platforms</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button
                    onClick={handleGenerateHashtags}
                    disabled={loadingHashtags || (!title && !description)}
                    variant="outline"
                    className="w-full h-10 border-zinc-700 hover:border-violet-500 mb-3"
                  >
                    {loadingHashtags ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Generate Optimized Hashtags
                  </Button>

                  {hashtags.length > 0 && (
                    <div className="space-y-3">
                      <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <div className="flex flex-wrap gap-1.5">
                          {hashtags.map((tag, index) => (
                            <span 
                              key={index}
                              className="px-2 py-1 text-xs bg-violet-500/20 text-violet-300 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {hashtagTips && (
                        <p className="text-xs text-zinc-500 italic">💡 {hashtagTips}</p>
                      )}
                      
                      <Button
                        onClick={copyHashtags}
                        variant="ghost"
                        size="sm"
                        className="w-full text-zinc-400 hover:text-zinc-200"
                      >
                        {copiedHashtags ? (
                          <>
                            <Check className="w-4 h-4 mr-2 text-green-500" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy All Hashtags
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center border-2 border-dashed border-zinc-800 rounded-xl min-h-[400px]">
                <div className="text-center p-8">
                  <Video className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">Your generated video will appear here</p>
                  <p className="text-sm text-zinc-600 mt-1">Upload images and click Generate</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Image Library Modal */}
      <Dialog open={showImageLibrary} onOpenChange={setShowImageLibrary}>
        <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Select Images from Library</DialogTitle>
          </DialogHeader>
          {loadingLibrary ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          ) : libraryImages.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">No images in library</p>
              <p className="text-sm text-zinc-600">Generate images first in Image Generator</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3 mt-4">
              {libraryImages.map((img) => (
                <button
                  key={img.id}
                  onClick={() => addImageFromLibrary(img)}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-zinc-800 hover:border-violet-500 transition-colors"
                >
                  <img
                    src={`data:image/png;base64,${img.image_base64}`}
                    alt={img.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Plus className="w-8 h-8 text-white" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audio Library Modal */}
      <Dialog open={showAudioLibrary} onOpenChange={setShowAudioLibrary}>
        <DialogContent className="max-w-2xl bg-zinc-950 border-zinc-800 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Select Audio from Library</DialogTitle>
          </DialogHeader>
          {loadingLibrary ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          ) : libraryAudios.length === 0 ? (
            <div className="text-center py-12">
              <Volume2 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">No audio in library</p>
              <p className="text-sm text-zinc-600">Generate audio first in Audio Generator</p>
            </div>
          ) : (
            <div className="space-y-2 mt-4">
              {libraryAudios.map((audio) => (
                <button
                  key={audio.id}
                  onClick={() => addAudioFromLibrary(audio)}
                  className="w-full p-4 rounded-lg border border-zinc-800 hover:border-violet-500 transition-colors text-left flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Volume2 className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-200 truncate">{audio.title}</p>
                    <p className="text-xs text-zinc-500 truncate">{audio.preview}</p>
                  </div>
                  <Plus className="w-5 h-5 text-zinc-400" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade to Pro Paywall Modal */}
      <PaywallModal
        open={showPaywall}
        onOpenChange={setShowPaywall}
        category={paywallCategory || 'youtube'}
        limit={paywallCategory === 'reel' ? (usageLimits?.reel?.limit ?? 1) : (usageLimits?.youtube?.limit ?? 1)}
      />
    </div>
  );
}
